'use client';

import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

import { LazyLoader } from '@hrworks/sui-core/LazyLoader';

import { filterPosts } from '../../data-access/filterPosts';
import { useUrlSearchParams } from '../../util/useUrlSearchParams';
import { Card, CardBody } from '../Card';
import { JobPost } from '../JobPost';
import { Search } from '../Search';
import type { JobListingsProps } from './JobListings.types';

const PAGE_SIZE = 20;

export const JobListings = ({ posts, searchOptions, companyId, remoteWorkName }: JobListingsProps) => {
  const t = useTranslations('Main');
  const [params] = useUrlSearchParams();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filterString = params.toString();
  const filters = useMemo(() => Object.fromEntries(new URLSearchParams(filterString).entries()), [filterString]);

  const flatPosts = useMemo(
    () =>
      filterPosts(posts, filters).sort((a, b) => {
        const priorityA = a.postOffer.priority || Number.POSITIVE_INFINITY;
        const priorityB = b.postOffer.priority || Number.POSITIVE_INFINITY;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return (a.postOffer.displayName || '').localeCompare(b.postOffer.displayName || '');
      }),
    [posts, filters],
  );

  const visiblePosts = flatPosts.slice(0, visibleCount);
  const hasMore = visibleCount < flatPosts.length;

  // Reset visible count when filters change
  const [prevFilterString, setPrevFilterString] = useState(filterString);
  if (filterString !== prevFilterString) {
    setPrevFilterString(filterString);
    setVisibleCount(PAGE_SIZE);
  }

  const loadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  return (
    <>
      <Search searchOptions={searchOptions} />
      {flatPosts.length === 0 && (
        <Card data-guide-id="joblist-no-results">
          <CardBody>{t('no-results')}</CardBody>
        </Card>
      )}
      <LazyLoader hasMoreBelow={hasMore} onFetchBelow={loadMore}>
        {visiblePosts.map(({ post, postOffer }) => (
          <JobPost
            companyId={companyId}
            post={post}
            postOffer={postOffer}
            key={postOffer.id}
            remoteWorkName={remoteWorkName}
          />
        ))}
      </LazyLoader>
    </>
  );
};
